import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import userServices from "../../services/userServices";
import { toast } from "react-toastify";
import RegisteredLobby from "./RegisteredLobby";
import CalendarShareDropdown from "../common/CalendarShareDropdown";
import {
  Calendar,
  CheckCircle,
  Clock,
  Users,
  ArrowDown,
  Play,
  HelpCircle,
} from "lucide-react";

const RLINK_PURPLE = "#832CFF";

/* ═══════════════════════════════════════════════════════════════════
   Event Landing Page layout components (mirrored from EventLandingPublic)
   Used when a webinar has a linked event landing page.
   The layouts wrap an arbitrary `formSlot` (the webinar's own registration
   form) instead of the standalone RegistrationForm used by EventLandingPublic.
   ═══════════════════════════════════════════════════════════════════ */

const formatELPDate = (d) => {
  if (!d) return "";
  try {
    const date = new Date(d);
    return `${date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
  } catch { return ""; }
};

const useCountdown = (targetIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return useMemo(() => {
    if (!targetIso) return null;
    const diff = Math.max(0, new Date(targetIso).getTime() - now);
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      isLive: diff === 0,
    };
  }, [targetIso, now]);
};

const ELPCountdownBar = ({ event }) => {
  const cd = useCountdown(event.scheduledStartTime);
  const showCountdown = event.showCountdownTimer && cd && !cd.isLive;
  const showScarcity = event.showLimitedSeatsWarning && event.remainingSpots != null;
  if (!showCountdown && !showScarcity) return null;
  const Box = ({ value, label }) => (
    <div className="flex flex-col items-center px-2 sm:px-3">
      <span className="text-2xl md:text-3xl font-bold tabular-nums">{String(value).padStart(2, "0")}</span>
      <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
    </div>
  );
  return (
    <div className="bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white py-3">
      <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
        {showCountdown && (
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5" /><span className="font-medium hidden sm:inline">Starts in</span>
            <div className="flex items-center divide-x divide-white/30">
              <Box value={cd.days} label="Days" /><Box value={cd.hours} label="Hrs" /><Box value={cd.minutes} label="Min" /><Box value={cd.seconds} label="Sec" />
            </div>
          </div>
        )}
        {showScarcity && (
          <div className="flex items-center gap-2 font-semibold"><Users className="h-5 w-5" />Only {event.remainingSpots} of {event.totalCapacity} spots left</div>
        )}
      </div>
    </div>
  );
};

const ELPDateBadge = ({ date, theme = "light" }) => (
  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${theme === "dark" ? "bg-white/10 text-white" : "bg-white text-gray-700 shadow"}`}>
    <Calendar className={`h-5 w-5 ${theme === "dark" ? "text-[#00c853]" : "text-[#6a1fbf]"}`} />
    <span className="font-medium">{formatELPDate(date)}</span>
  </div>
);

const ELPSpeakersGrid = ({ speakers, theme = "light" }) => {
  if (!speakers?.length) return null;
  const isDark = theme === "dark";
  return (
    <div className={`grid md:grid-cols-2 ${speakers.length > 2 ? "lg:grid-cols-3" : ""} gap-6`}>
      {speakers.map((s, i) => (
        <div key={i} className={`text-center p-5 rounded-2xl ${isDark ? "bg-white/5 border border-white/10" : "bg-white shadow-sm border border-gray-100"}`}>
          {(s.photoUrl || s.photo_url) ? <img src={s.photoUrl || s.photo_url} alt={s.name} className="w-24 h-24 rounded-full mx-auto mb-3 object-cover" /> : <div className={`w-24 h-24 rounded-full mx-auto mb-3 ${isDark ? "bg-white/10" : "bg-gray-100"}`} />}
          <h4 className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{s.name}</h4>
          {s.title && <p className="text-[#6a1fbf] font-medium text-sm">{s.title}</p>}
          {s.bio && <p className={`text-sm mt-2 ${isDark ? "text-white/70" : "text-gray-600"}`}>{s.bio}</p>}
        </div>
      ))}
    </div>
  );
};

const ELPSocialProof = ({ items, theme = "light" }) => {
  if (!items?.length) return null;
  const isDark = theme === "dark";
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {items.map((p, i) => (
        <div key={i} className={`rounded-xl p-5 ${isDark ? "bg-white/5 border border-white/10" : "bg-gray-50 border border-gray-100"}`}>
          {p.type === "logo" && (p.logoUrl || p.logo_url) ? <img src={p.logoUrl || p.logo_url} alt="Logo" className="h-10 mx-auto opacity-80" /> : (
            <><p className={`italic mb-2 ${isDark ? "text-white/80" : "text-gray-700"}`}>"{p.content}"</p>{p.author && <p className={`font-semibold text-sm ${isDark ? "text-white" : "text-gray-900"}`}>— {p.author}</p>}</>
          )}
        </div>
      ))}
    </div>
  );
};

const scrollToForm = () => document.getElementById("webinar-reg-form")?.scrollIntoView({ behavior: "smooth" });

// Shared section: Host & Presenters (shown on all layouts when webinar data is available)
const WebinarHostSection = ({ webinar, theme = "light" }) => {
  const isDark = theme === "dark";
  const people = [];
  if (webinar.hostName) people.push({ name: webinar.hostName, role: "Host" });
  (webinar.presenters || []).forEach(p => people.push({ name: p.name, role: "Presenter" }));
  (webinar.coHosts || []).forEach(p => people.push({ name: p.name, role: "Co-Host" }));
  if (!people.length) return null;

  return (
    <div className={`rounded-2xl p-6 ${isDark ? "bg-white/5 border border-white/10" : "bg-white shadow-sm border border-gray-100"}`}>
      <h3 className={`text-lg font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Your Host & Presenters</h3>
      <div className="space-y-3">
        {people.map((p, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${isDark ? "bg-white/10 text-white" : "bg-gradient-to-br from-[#6a1fbf] to-[#00c853] text-white"}`}>
              {p.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{p.name}</div>
              <div className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}>{p.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Shared section: Hero Image from webinar
const WebinarHeroImage = ({ webinar, className = "" }) => {
  const heroImage = webinar.registrationHeroImage || webinar.registrationCoverImage;
  if (!heroImage) return null;
  return (
    <div className={`rounded-2xl overflow-hidden ${className}`}>
      <img src={heroImage} alt={webinar.roomName || "Webinar"} className="w-full h-auto object-cover" />
    </div>
  );
};

// Layout wrappers — each receives `event` (the landing page data), `formSlot` (the webinar's registration form JSX), and `webinar`
const ELPClassicSaas = ({ event, formSlot, webinar }) => (
  <div className="min-h-screen bg-gray-50">
    <ELPCountdownBar event={event} />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div>
          {event.logoUrl && <img src={event.logoUrl} alt="Logo" className="h-12 mb-6" />}
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight">{event.heroHeadline || event.yourName}</h1>
          {event.heroSubheadline && <p className="text-lg md:text-xl text-gray-600 mb-7">{event.heroSubheadline}</p>}
          {event.heroBullets?.length > 0 && (
            <ul className="space-y-3 mb-7">{event.heroBullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3"><CheckCircle className="w-6 h-6 text-[#00c853] flex-shrink-0 mt-0.5" /><span className="text-base text-gray-700">{b}</span></li>
            ))}</ul>
          )}
          <ELPDateBadge date={event.scheduledStartTime} />
          <WebinarHeroImage webinar={webinar} className="mt-8" />
          <div className="mt-6"><WebinarHostSection webinar={webinar} /></div>
        </div>
        <div id="webinar-reg-form">{formSlot}</div>
      </div>
    </div>
  </div>
);

const ELPCenteredBigPromise = ({ event, formSlot, webinar }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
    <ELPCountdownBar event={event} />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
      {event.logoUrl && <img src={event.logoUrl} alt="Logo" className="h-16 mx-auto mb-8" />}
      <div className="inline-block bg-[#6a1fbf]/10 text-[#6a1fbf] px-4 py-2 rounded-full text-sm font-semibold mb-6">
        {event.format === "evergreen" ? "Free On-Demand Training" : "Free Live Training"}
      </div>
      <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">{event.heroHeadline || event.yourName}</h1>
      {event.heroSubheadline && <p className="text-xl lg:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto">{event.heroSubheadline}</p>}
      <WebinarHeroImage webinar={webinar} className="max-w-2xl mx-auto mb-8" />
      <div className="flex justify-center mb-8"><ELPDateBadge date={event.scheduledStartTime} /></div>
      <button onClick={scrollToForm} className="inline-flex items-center gap-2 px-12 py-5 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white text-lg font-semibold hover:shadow-2xl hover:shadow-purple-500/30 transition-all">
        {event.ctaButtonText || "Register Now"}<ArrowDown className="w-5 h-5" />
      </button>
    </div>
    <div className="bg-white border-t border-gray-200 py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-8"><WebinarHostSection webinar={webinar} /></div>
      <div id="webinar-reg-form" className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">{formSlot}</div>
    </div>
  </div>
);

const ELPBackgroundCard = ({ event, formSlot, webinar }) => {
  const heroImage = webinar?.registrationHeroImage || webinar?.registrationCoverImage;
  const bgStyle = event.heroImageUrl ? { backgroundImage: `url(${event.heroImageUrl})` }
    : heroImage ? { backgroundImage: `url(${heroImage})` }
    : { background: "linear-gradient(135deg, #001233 0%, #1a1052 50%, #00482b 100%)" };
  return (
    <div className="min-h-screen bg-gray-50">
      <ELPCountdownBar event={event} />
      <div className="relative bg-cover bg-center py-16 lg:py-28" style={bgStyle}>
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            {event.logoUrl && <img src={event.logoUrl} alt="Logo" className="h-12 mb-6" />}
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{event.heroHeadline || event.yourName}</h1>
            {event.heroSubheadline && <p className="text-xl text-gray-600 mb-6">{event.heroSubheadline}</p>}
            <div className="mb-8"><ELPDateBadge date={event.scheduledStartTime} /></div>
            {event.heroBullets?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What You'll Get:</h3>
                <ul className="space-y-2.5">{event.heroBullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#00c853] flex-shrink-0 mt-0.5" /><span className="text-gray-700">{b}</span></li>
                ))}</ul>
              </div>
            )}
            <div className="mb-6"><WebinarHostSection webinar={webinar} /></div>
            <div id="webinar-reg-form">{formSlot}</div>
          </div>
        </div>
      </div>
      {(event.speakers?.length > 0 || event.socialProof?.length > 0) && (
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            {event.speakers?.length > 0 && <div><h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Your Speakers</h2><ELPSpeakersGrid speakers={event.speakers} /></div>}
            {event.socialProof?.length > 0 && <div><h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Loved by professionals</h2><ELPSocialProof items={event.socialProof} /></div>}
          </div>
        </div>
      )}
    </div>
  );
};

const ELPAgendaFirst = ({ event, formSlot, webinar }) => (
  <div className="min-h-screen bg-gray-50">
    <ELPCountdownBar event={event} />
    <div className="bg-gradient-to-br from-[#6a1fbf] to-[#00c853] text-white py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {event.logoUrl && <img src={event.logoUrl} alt="Logo" className="h-14 mx-auto mb-6 brightness-0 invert" />}
        <h1 className="text-4xl lg:text-5xl font-bold mb-6">{event.heroHeadline || event.yourName}</h1>
        {event.heroSubheadline && <p className="text-xl lg:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">{event.heroSubheadline}</p>}
        <div className="flex items-center justify-center gap-2 text-white mb-8">
          <Calendar className="w-5 h-5" /><span className="text-lg font-medium">{formatELPDate(event.scheduledStartTime)}</span>
        </div>
        <button onClick={scrollToForm} className="inline-flex items-center gap-2 px-12 py-5 rounded-lg bg-white text-[#6a1fbf] hover:bg-gray-100 text-lg font-semibold transition-all">
          {event.ctaButtonText || "Register Now"}<ArrowDown className="w-5 h-5" />
        </button>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <WebinarHeroImage webinar={webinar} className="mb-12 max-w-3xl mx-auto" />
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What You'll Learn</h2>
          {event.agenda?.length > 0 ? (
            <ul className="space-y-4">{event.agenda.map((item, i) => (
              <li key={i} className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</div>
                <span className="text-base text-gray-700 pt-1.5">{typeof item === "string" ? item : item.title}</span>
              </li>
            ))}</ul>
          ) : <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-gray-600">Agenda details coming soon...</div>}
          <div className="mt-8"><WebinarHostSection webinar={webinar} /></div>
        </div>
        <div className="space-y-8">
          {event.speakers?.length > 0 && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Meet Your Hosts</h2>
              <div className="space-y-4">{event.speakers.map((s, i) => (
                <div key={i} className="flex gap-4 bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  {(s.photoUrl || s.photo_url) && <img src={s.photoUrl || s.photo_url} alt={s.name} className="w-20 h-20 rounded-full object-cover flex-shrink-0" />}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{s.name}</h3>
                    {s.title && <p className="text-[#6a1fbf] font-medium mb-1">{s.title}</p>}
                    {s.bio && <p className="text-gray-600 text-sm">{s.bio}</p>}
                  </div>
                </div>
              ))}</div>
            </div>
          )}
          <div id="webinar-reg-form" className="lg:sticky lg:top-8">{formSlot}</div>
        </div>
      </div>
    </div>
  </div>
);

const ELPEvergreenOnDemand = ({ event, formSlot, webinar }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 text-white">
    <ELPCountdownBar event={event} />
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
      <div className="text-center mb-12">
        {event.logoUrl && <img src={event.logoUrl} alt="Logo" className="h-14 mx-auto mb-6 brightness-0 invert" />}
        <div className="inline-block bg-[#00c853] text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
          {event.evergreenCtaType === "time_slot_picker" ? "Choose Your Time" : "Watch Instantly"}
        </div>
        <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">{event.heroHeadline || event.yourName}</h1>
        {event.heroSubheadline && <p className="text-xl lg:text-2xl text-white/80 mb-8 max-w-3xl mx-auto">{event.heroSubheadline}</p>}
        <WebinarHeroImage webinar={webinar} className="max-w-2xl mx-auto mb-8 rounded-2xl" />
      </div>
      <div className="max-w-xl mx-auto mb-8"><WebinarHostSection webinar={webinar} theme="dark" /></div>
      <div id="webinar-reg-form" className="max-w-xl mx-auto">{formSlot}</div>
    </div>
    {event.socialProof?.length > 0 && (
      <div className="bg-white/5 backdrop-blur-lg py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-8">What Others Are Saying</h2>
          <ELPSocialProof items={event.socialProof} theme="dark" />
        </div>
      </div>
    )}
    {event.faq?.length > 0 && (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-center mb-8 flex items-center justify-center gap-2"><HelpCircle className="w-7 h-7" /> Frequently Asked Questions</h2>
        <div className="space-y-4">{event.faq.map((item, i) => (
          <div key={i} className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/15">
            <h3 className="text-lg font-bold mb-2">{item.question}</h3>
            <p className="text-white/80">{item.answer}</p>
          </div>
        ))}</div>
      </div>
    )}
  </div>
);

const ELP_LAYOUT_MAP = {
  "classic-saas": ELPClassicSaas,
  "centered-big-promise": ELPCenteredBigPromise,
  "background-card": ELPBackgroundCard,
  "agenda-first": ELPAgendaFirst,
  "evergreen-on-demand": ELPEvergreenOnDemand,
};

function WebinarRegistrationPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Webinar data
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // True while we're checking the signed-in viewer's role server-side to
  // decide whether to auto-bypass the registration page. Gates the form
  // render so the registration UI doesn't briefly flicker into view before
  // a host/co-host gets redirected into the room.
  const [roleCheckPending, setRoleCheckPending] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    customFields: {},
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Registration/view state
  const [isRegistered, setIsRegistered] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showReplay, setShowReplay] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [joinUrl, setJoinUrl] = useState("");

  // Verify email state (for already registered users)
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  // Replay registration form (shown when unregistered email tries to watch replay)
  const [showReplayRegForm, setShowReplayRegForm] = useState(false);

  // Passcode state
  const [showPasscodeGate, setShowPasscodeGate] = useState(false);
  const [passcode, setPasscode] = useState(["", "", "", ""]);
  const [passcodeError, setPasscodeError] = useState("");
  const passcodeRefs = [useRef(), useRef(), useRef(), useRef()];

  // Countdown
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Video player
  const playerRef = useRef(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState("");

  // Fetch webinar info
  useEffect(() => {
    const fetchWebinar = async () => {
      try {
        const response = await userServices.getPublicWebinarInfo(id);
        if (response.success) {
          setWebinar(response.webinar);
          if (response.webinar.recordingUrl) {
            setRecordingUrl(response.webinar.recordingUrl);
          }
        } else {
          setError("Webinar not found");
        }
      } catch (err) {
        console.error("Error fetching webinar:", err);
        setError("Unable to load webinar information");
      } finally {
        setLoading(false);
      }
    };
    fetchWebinar();
  }, [id]);

  // Auto-bypass: if the visitor is already signed in AND is the host or a
  // co-host of THIS webinar, skip the registration page entirely and drop
  // them straight into the meeting room. The /element/meeting-by-name
  // endpoint is optionally authenticated — when called with a Bearer token
  // it returns the caller's role for the meeting; the relevant roles are
  // computed server-side in elementController.getRole() against
  // roomCreatedBy / roomOwnerId / coHosts.
  useEffect(() => {
    if (!webinar?.roomId) return;
    // Don't bypass for past webinars — hosts/co-hosts visiting an ended
    // webinar's event link should land on the existing Replay view instead
    // of being shoved into a dead room.
    if (webinar.isPast) return;
    if (typeof localStorage === 'undefined') return;
    const token = localStorage.getItem('token');
    if (!token) return; // not signed in → show normal registration flow
    // Escape hatch: hosts can append ?preview=1 (or &preview=1) to view their
    // own registration page exactly as attendees see it without getting
    // auto-redirected into the room. Useful for proofreading copy / testing
    // the public flow without having to sign out or use incognito.
    const previewMode = new URLSearchParams(location.search).get('preview') === '1';
    if (previewMode) return;

    setRoleCheckPending(true);
    let cancelled = false;
    (async () => {
      try {
        const { getMeetingByName } = await import('../../services/meetingServices');
        const res = await getMeetingByName(webinar.roomId);
        if (cancelled) return;
        const role = res?.meeting?.role;
        if (role && ['moderator', 'co-host', 'host'].includes(role)) {
          // Preserve any existing query string (e.g. ?r=token) so downstream
          // logic in Conference.js still has whatever context the link
          // carried. Don't clear roleCheckPending here — the pending state
          // keeps the loading spinner up through the navigation, avoiding
          // the registration-form flash on the way out.
          const qs = location.search || '';
          navigate(`/${webinar.roomId}${qs}`, { replace: true });
          return;
        }
        // Authenticated but not a host/co-host → fall through to the form.
        setRoleCheckPending(false);
      } catch (err) {
        // Silent: 401/expired token just means the user falls through to the
        // normal registration form. We don't want a transient API blip to
        // strand the page.
        console.warn('[WebinarRegistration] role-bypass check failed:', err?.message);
        if (!cancelled) setRoleCheckPending(false);
      }
    })();
    return () => { cancelled = true; };
  }, [webinar?.roomId, webinar?.isPast, location.search, navigate]);

  // Redirect /register → /event when registration is not required
  useEffect(() => {
    if (!webinar) return;
    if (!webinar.requireRegistration && location.pathname.endsWith("/register")) {
      navigate(`/webinar/${id}/event`, { replace: true });
    }
  }, [webinar, location.pathname, navigate, id]);

  // Resolve `?r=<token>` param (email-link arrivals): pre-fill the form and
  // seed localStorage so the standard registered-user branches below take
  // over. This is what makes the email join link skip the registration form
  // on return visits and land the attendee directly on the "You're
  // registered — countdown / Join Now / Replay" view.
  useEffect(() => {
    if (!webinar) return;
    const params = new URLSearchParams(location.search);
    const token = params.get('r');
    if (!token) return;
    const existing = localStorage.getItem(`webinar_reg_${webinar.roomId}`);
    if (existing) {
      try {
        if (JSON.parse(existing).token === token) return; // already resolved
      } catch { /* fall through and overwrite */ }
    }
    (async () => {
      try {
        const { resolveRegistrationByToken } = await import('../../services/meetingServices');
        const res = await resolveRegistrationByToken(webinar.roomId, token);
        if (res?.success && res.registrant) {
          const data = {
            firstName: res.registrant.firstName || '',
            lastName: res.registrant.lastName || '',
            email: res.registrant.email || '',
            token,
          };
          localStorage.setItem(`webinar_reg_${webinar.roomId}`, JSON.stringify(data));
          setFormData((prev) => ({ ...prev, ...data }));
          setIsRegistered(true);
          // Non-replay: show the confirmation/countdown view. The existing
          // confirmation render below will show Join Now when the webinar
          // is already live or a countdown + Add to Calendar when it's not.
          if (!webinar.isPast) setShowConfirmation(true);
          if (webinar.isLive) setJoinUrl(`/${webinar.roomId}?r=${encodeURIComponent(token)}`);
          // Past + replay allowed? Surface the replay immediately.
          if (webinar.isPast && !webinar.requirePasscode) {
            if (!webinar.requireRegistrationForReplay || data.email) setShowReplay(true);
          }
        }
      } catch (err) {
        console.warn('[WebinarRegistration] token lookup failed:', err?.message);
      }
    })();
  }, [webinar, location.search]);

  // Check if user previously registered (from localStorage)
  useEffect(() => {
    if (!webinar) return;
    const stored = localStorage.getItem(`webinar_reg_${webinar.roomId}`);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setIsRegistered(true);
        setFormData((prev) => ({
          ...prev,
          firstName: data.firstName || prev.firstName,
          lastName: data.lastName || prev.lastName,
          email: data.email || prev.email,
        }));
        // If webinar is live and already registered, show confirmation (with Join button)
        if (webinar.isLive) {
          setShowConfirmation(true);
          // Preserve the registration token in the Join Now link so the
          // live-bypass path in Conference.js can identify the attendee.
          const t = data.token ? `?r=${encodeURIComponent(data.token)}` : '';
          setJoinUrl(`/${webinar.roomId}${t}`);
        }
        // For future webinars with a registered user, surface the confirmation
        // card so they see "You're registered — countdown" instead of the
        // registration form.
        if (!webinar.isLive && !webinar.isPast) {
          setShowConfirmation(true);
        }
        // If past and replay available, show replay directly
        if (webinar.isPast && !webinar.requirePasscode) {
          if (!webinar.requireRegistrationForReplay || data.email) {
            setShowReplay(true);
          }
        }
      } catch (e) {
        // ignore
      }
    }
  }, [webinar]);

  // Poll public status to keep this page in sync with the webinar lifecycle:
  //   - Before live: detect host clicking "Start Webinar" (isLive flips true)
  //     so the countdown view automatically swaps to the Join Now button.
  //     Registered attendees are auto-redirected into the room.
  //   - During live: detect host ending the session so we flip to replay
  //     without forcing the attendee to refresh.
  // Polling interval is shorter pre-live (10s) so the live-transition feels
  // immediate, and longer once live (30s) since end-of-session is less
  // time-sensitive.
  useEffect(() => {
    if (!webinar || webinar.isPast) return;
    const intervalMs = webinar.isLive ? 30000 : 10000;
    const id = setInterval(async () => {
      try {
        const res = await userServices.getPublicWebinarInfo(webinar.roomId);
        if (!res?.success || !res.webinar) return;
        const fresh = res.webinar;
        // End-of-session (live → past)
        if (fresh.isPast || fresh.currentStatus === 'ended') {
          setWebinar(fresh);
          if (fresh.recordingUrl) {
            setRecordingUrl(fresh.recordingUrl);
            setShowReplay(true);
            setShowConfirmation(false);
          }
          return;
        }
        // Pre-live → live transition
        if (!webinar.isLive && fresh.isLive) {
          setWebinar(fresh);
          const stored = localStorage.getItem(`webinar_reg_${fresh.roomId}`);
          if (stored) {
            try {
              const data = JSON.parse(stored);
              const t = data?.token ? `?r=${encodeURIComponent(data.token)}` : '';
              // Auto-redirect registered attendees straight into the room.
              navigate(`/${fresh.roomId}${t}`);
            } catch {
              navigate(`/${fresh.roomId}`);
            }
          }
        }
      } catch { /* ignore transient errors, keep polling */ }
    }, intervalMs);
    return () => clearInterval(id);
  }, [webinar, navigate]);

  // Countdown timer
  const calculateTimeDifference = useCallback(() => {
    if (!webinar?.scheduledStartTime) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    try {
      const eventTime = new Date(webinar.scheduledStartTime);
      const now = new Date();
      const diffMs = eventTime.getTime() - now.getTime();
      if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      const totalSec = Math.floor(diffMs / 1000);
      return {
        days: Math.floor(totalSec / (3600 * 24)),
        hours: Math.floor((totalSec % (3600 * 24)) / 3600),
        minutes: Math.floor((totalSec % 3600) / 60),
        seconds: totalSec % 60,
      };
    } catch {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  }, [webinar?.scheduledStartTime]);

  useEffect(() => {
    if (!webinar || webinar.isPast) return;
    setTimeLeft(calculateTimeDifference());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeDifference());
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeDifference, webinar]);

  // Helper to check if a field is enabled/required based on registrationFields
  const getFieldConfig = (fieldName) => {
    const fields = webinar?.registrationFields;
    if (!fields || fields.length === 0) {
      // Default: First Name, Last Name, Email always shown & required
      const defaults = ["First Name", "Last Name", "Email Address"];
      return { enabled: defaults.includes(fieldName), required: defaults.includes(fieldName) };
    }
    const field = fields.find((f) => f.name === fieldName);
    return { enabled: field?.enabled ?? false, required: field?.required ?? false };
  };

  // Validation
  const validateForm = () => {
    const errors = {};
    const fn = getFieldConfig("First Name");
    if (fn.enabled && fn.required && !formData.firstName.trim()) errors.firstName = "First name is required";
    const ln = getFieldConfig("Last Name");
    if (ln.enabled && ln.required && !formData.lastName.trim()) errors.lastName = "Last name is required";
    const em = getFieldConfig("Email Address");
    if (em.enabled && em.required) {
      if (!formData.email.trim()) {
        errors.email = "Email is required";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim()))
          errors.email = "Please enter a valid email";
      }
    }
    const ph = getFieldConfig("Phone Number");
    if (ph.enabled && ph.required && !formData.phone.trim()) errors.phone = "Phone number is required";
    // Validate custom fields
    const knownFields = ["First Name", "Last Name", "Email Address", "Phone Number"];
    const customFields = (webinar?.registrationFields || []).filter(f => f.enabled && !knownFields.includes(f.name));
    customFields.forEach(f => {
      if (f.required && !(formData.customFields[f.name] || "").trim()) {
        errors[`custom_${f.name}`] = `${f.name} is required`;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Pre-register (before event)
  const handlePreRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const response = await userServices.preRegister({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        meetingId: webinar.roomId,
        customFields: formData.customFields && Object.keys(formData.customFields).length > 0 ? formData.customFields : undefined,
      });
      if (response.success) {
        setIsRegistered(true);
        setShowConfirmation(true);
        setJoinUrl(response.joinUrl || `/${webinar.roomId}`);
        localStorage.setItem(
          `webinar_reg_${webinar.roomId}`,
          JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
          })
        );
        toast.success("Registration successful!");
      } else {
        // Already registered: route to verify-email flow regardless of live state
        // so pending/approved attendees can resume their registration without
        // being stuck on the form. We pre-fill the email they just typed.
        if (response.message?.toLowerCase().includes("already registered")) {
          setShowVerifyEmail(true);
          setVerifyEmail(formData.email);
          toast.info("You're already registered. Verify your email to continue.");
        } else if (response.message?.toLowerCase().includes("pending")) {
          toast.info(response.message);
        } else {
          toast.error(response.message || "Registration failed");
        }
      }
    } catch (err) {
      const msg = err?.message || "";
      if (msg.toLowerCase().includes("already registered")) {
        setShowVerifyEmail(true);
        setVerifyEmail(formData.email);
        toast.info("You're already registered. Verify your email to continue.");
      } else if (msg.toLowerCase().includes("pending")) {
        toast.info(msg);
      } else {
        toast.error(msg || "Registration failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Post-register (replay access)
  const handlePostRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // If passcode required, show gate first
    if (webinar.requirePasscode && !showPasscodeGate) {
      setShowPasscodeGate(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const passcodeStr = passcode.join("");
      const response = await userServices.postRegister({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        meetingId: webinar.roomId,
        passcode: webinar.requirePasscode ? passcodeStr : undefined,
        customFields: formData.customFields && Object.keys(formData.customFields).length > 0 ? formData.customFields : undefined,
      });
      if (response.success) {
        setIsRegistered(true);
        setShowReplay(true);
        if (response.recording) {
          setRecordingUrl(response.recording);
        }
        localStorage.setItem(
          `webinar_reg_${webinar.roomId}`,
          JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
          })
        );
        toast.success("Access granted!");
      } else {
        if (response.message?.toLowerCase().includes("passcode")) {
          setPasscodeError("Invalid passcode");
        } else {
          toast.error(response.message || "Registration failed");
        }
      }
    } catch (err) {
      const msg = err.message || "Registration failed";
      if (msg.toLowerCase().includes("passcode")) {
        setPasscodeError("Invalid passcode");
      } else {
        toast.error(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Verify registration by email (for already registered users)
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!verifyEmail.trim()) {
      setVerifyError("Please enter your email");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(verifyEmail.trim())) {
      setVerifyError("Please enter a valid email");
      return;
    }
    setIsVerifying(true);
    setVerifyError("");
    try {
      const response = await userServices.verifyRegistration({
        email: verifyEmail.trim(),
        meetingId: webinar.roomId,
      });
      if (response.success) {
        // Store in localStorage so they won't be redirected again
        localStorage.setItem(
          `webinar_reg_${webinar.roomId}`,
          JSON.stringify({
            firstName: response.registrant?.firstName || "",
            lastName: response.registrant?.lastName || "",
            email: verifyEmail.trim(),
          })
        );
        setIsRegistered(true);
        setShowConfirmation(true);
        setShowVerifyEmail(false);
        setJoinUrl(response.joinUrl || `/${webinar.roomId}`);
        toast.success("Email verified! Click Join to enter.");
      } else {
        setVerifyError(response.message || "Email not found. Please register first.");
      }
    } catch (err) {
      setVerifyError(err.message || "Verification failed. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  // Passcode input handlers
  const handlePasscodeChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newPasscode = [...passcode];
    newPasscode[index] = value;
    setPasscode(newPasscode);
    setPasscodeError("");
    if (value && index < 3) {
      passcodeRefs[index + 1].current?.focus();
    }
  };

  const handlePasscodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !passcode[index] && index > 0) {
      passcodeRefs[index - 1].current?.focus();
    }
  };

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    const code = passcode.join("");
    if (code.length !== 4) {
      setPasscodeError("Please enter all 4 digits");
      return;
    }
    // Re-submit with passcode
    handlePostRegister(e);
  };

  // Format event time
  const formatEventTime = () => {
    if (!webinar?.scheduledStartTime) return "Time TBD";
    try {
      const tz = webinar.timezone || undefined;
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      let result = formatter.format(new Date(webinar.scheduledStartTime));
      // Append timezone abbreviation
      if (tz) {
        try {
          const tzParts = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            timeZoneName: "short",
          }).formatToParts(new Date(webinar.scheduledStartTime));
          const tzName = tzParts.find((p) => p.type === "timeZoneName")?.value;
          if (tzName) result += ` (${tzName})`;
        } catch {}
      }
      return result;
    } catch {
      return new Date(webinar.scheduledStartTime).toLocaleString();
    }
  };

  const formatTime = (n) => String(n).padStart(2, "0");

  // ─── LOADING / ERROR ───
  // `roleCheckPending` is folded into the loading gate so that signed-in
  // hosts/co-hosts don't see the registration UI flicker into view before
  // their auto-redirect lands.
  if (loading || roleCheckPending) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Loading webinar...</p>
        </div>
      </div>
    );
  }

  if (error || !webinar) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-white text-2xl font-bold mb-2">Webinar Not Found</h1>
          <p className="text-gray-400">{error || "This webinar does not exist."}</p>
        </div>
      </div>
    );
  }

  const isPast = webinar.isPast;
  const heroImage = webinar.registrationHeroImage || webinar.registrationCoverImage;
  const headline = webinar.roomName || webinar.registrationTitle;
  const description = webinar.description || webinar.registrationDescription || webinar.registrationSubtitle || "";
  const requiresReplayRegistration = webinar.requireRegistrationForReplay !== false;

  // ─── REPLAY VIEW ───
  // Show replay directly if registration is not required for replay
  if ((showReplay || (isPast && !requiresReplayRegistration)) && isPast) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between h-14 px-6 border-b border-white/10 bg-black/50 backdrop-blur-sm">
          <RLinkIcon />
          <span className="text-gray-400 text-xs font-medium tracking-wider uppercase">Replay</span>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
          <div className="w-full max-w-4xl">
            <h1 className="text-white text-2xl font-bold mb-2 text-center">{headline}</h1>
            <p className="text-gray-400 text-center mb-8">
              You're in. Enjoy the replay.
            </p>
            {/* Video Player */}
            <div className="w-full aspect-video bg-black rounded-xl overflow-hidden relative border border-white/10">
              {videoError ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <svg className="w-12 h-12 text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-400 text-sm">Recording not available yet</p>
                  <p className="text-gray-500 text-xs mt-1">Please check back later</p>
                </div>
              ) : recordingUrl ? (
                <>
                  {videoLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
                      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  <ReactPlayer
                    ref={playerRef}
                    url={recordingUrl}
                    width="100%"
                    height="100%"
                    controls
                    onError={() => setVideoError("Failed to load video")}
                    onReady={() => setVideoLoading(false)}
                    config={{
                      file: {
                        attributes: { controlsList: "nodownload", preload: "auto" },
                      },
                    }}
                  />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  <svg className="w-12 h-12 text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-400 text-sm">Recording not available yet</p>
                  <p className="text-gray-500 text-xs mt-1">Please check back later</p>
                </div>
              )}
            </div>
          </div>
          {/* Powered By */}
          <div className="mt-12 flex items-center gap-2">
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">Powered by</span>
            <span className="text-white text-xs font-semibold">R-Link</span>
          </div>
        </div>
      </div>
    );
  }

  // (Passcode gate for replay is now inline in the simplified replay form below)

  // ─── CONFIRMATION / LOBBY SCREEN (after pre-register, before live) ───
  // Registered attendees see the RegisteredLobby — a branded waiting room
  // with countdown, host info, mic/cam pre-check, and a clear "we'll take
  // you in automatically" banner. When the host hits Start, the parent's
  // 10s polling flips webinar.isLive → true and auto-navigates us into
  // the room (see the polling effect above).
  // Previously this early-returned a minimal dark <RegisteredLobby> layout
  // when the attendee was already registered. That layout lost the hero
  // image, branding, and event framing that the regular registration page
  // carries. We now fall through to the default layout and swap only the
  // form slot for the confirmation content — see the showConfirmation
  // branch inside the form area below.

  // ─── EVENT LANDING PAGE LAYOUT (when linked) ───
  // If the webinar has a linked event landing page, render the webinar's
  // registration form inside the event landing page's visual layout.
  if (process.env.REACT_APP_ENABLE_EVENT_LANDING_LAYOUT === "true" && webinar.eventLandingPage) {
    const elp = webinar.eventLandingPage;
    const Layout = ELP_LAYOUT_MAP[elp.layoutType] || ELPClassicSaas;

    // Build the webinar's registration form as a slot to inject into the layout
    const webinarFormSlot = (
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
        {/* Confirmation state */}
        {showConfirmation && !isPast ? (
          <div className="text-center">
            <CheckCircle className="h-14 w-14 text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">You're registered!</h3>
            <p className="text-gray-600 mb-4">We've saved your spot. Check your email for details.</p>
            {joinUrl && (
              <a href={joinUrl} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white font-semibold hover:shadow-lg transition-all">
                Join Now
              </a>
            )}
          </div>
        ) : (
          <>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
              {isPast ? "Watch the Replay" : (headline || "Save Your Spot")}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {isPast ? "Enter your details to access the replay." : "Reserve your free seat below."}
            </p>

            {/* Live Now Banner */}
            {!isPast && webinar.isLive && (
              <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <span className="text-green-700 text-sm font-semibold">This webinar is live now!</span>
                </div>
              </div>
            )}

            {!isPast && !webinar.requireRegistration ? (
              <div className="space-y-4">
                {webinar.isLive ? (
                  <a href={`/${webinar.roomId}`} className="block w-full py-3.5 rounded-xl font-semibold text-white text-center transition-all hover:opacity-90 bg-gradient-to-r from-[#6a1fbf] to-[#00c853]">Join Now</a>
                ) : (
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-center">
                    <p className="text-gray-600 text-sm">This event does not require registration.</p>
                    <p className="text-gray-400 text-xs mt-1">The join link will be available when the event goes live.</p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={showReplayRegForm ? handlePostRegister : handlePreRegister} className="space-y-4">
                {getFieldConfig("First Name").enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name {getFieldConfig("First Name").required && <span className="text-red-500">*</span>}</label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="John"
                      className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900 ${formErrors.firstName ? "border-red-500" : "border-gray-300 focus:border-[#6a1fbf]"}`} />
                    {formErrors.firstName && <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>}
                  </div>
                )}
                {getFieldConfig("Last Name").enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name {getFieldConfig("Last Name").required && <span className="text-red-500">*</span>}</label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Doe"
                      className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900 ${formErrors.lastName ? "border-red-500" : "border-gray-300 focus:border-[#6a1fbf]"}`} />
                    {formErrors.lastName && <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>}
                  </div>
                )}
                {getFieldConfig("Email Address").enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address {getFieldConfig("Email Address").required && <span className="text-red-500">*</span>}</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com"
                      className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900 ${formErrors.email ? "border-red-500" : "border-gray-300 focus:border-[#6a1fbf]"}`} />
                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                  </div>
                )}
                {getFieldConfig("Phone Number").enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number {getFieldConfig("Phone Number").required && <span className="text-red-500">*</span>}</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+1 (555) 000-0000"
                      className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900 ${formErrors.phone ? "border-red-500" : "border-gray-300 focus:border-[#6a1fbf]"}`} />
                    {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                  </div>
                )}
                {/* Custom Registration Fields */}
                {(webinar?.registrationFields || [])
                  .filter(f => f.enabled && !["First Name", "Last Name", "Email Address", "Phone Number"].includes(f.name))
                  .map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{field.name} {field.required && <span className="text-red-500">*</span>}</label>
                      {field.name === "Questions/Comments" ? (
                        <textarea value={(formData.customFields || {})[field.name] || ""} onChange={(e) => setFormData(prev => ({ ...prev, customFields: { ...(prev.customFields || {}), [field.name]: e.target.value } }))}
                          placeholder={field.name} rows={3} className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900 resize-none ${formErrors[`custom_${field.name}`] ? "border-red-500" : "border-gray-300 focus:border-[#6a1fbf]"}`} />
                      ) : (
                        <input type="text" value={(formData.customFields || {})[field.name] || ""} onChange={(e) => setFormData(prev => ({ ...prev, customFields: { ...(prev.customFields || {}), [field.name]: e.target.value } }))}
                          placeholder={field.name} className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900 ${formErrors[`custom_${field.name}`] ? "border-red-500" : "border-gray-300 focus:border-[#6a1fbf]"}`} />
                      )}
                      {formErrors[`custom_${field.name}`] && <p className="text-red-500 text-xs mt-1">{formErrors[`custom_${field.name}`]}</p>}
                    </div>
                  ))}
                <button type="submit" disabled={isSubmitting}
                  className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white font-semibold hover:shadow-lg transition-all disabled:opacity-60">
                  {isSubmitting ? "Registering..." : isPast ? "Watch Replay" : webinar.isLive ? "Register & Join Now" : (elp.ctaButtonText || "Register Now")}
                </button>
              </form>
            )}

            {/* Already registered? Verify */}
            {!isPast && !showVerifyEmail && webinar.requireRegistration && (
              <div className="mt-4 text-center">
                <button onClick={() => setShowVerifyEmail(true)} className="text-[#6a1fbf] text-base font-semibold hover:underline">
                  Already registered? Verify your email to join
                </button>
              </div>
            )}
            {!isPast && showVerifyEmail && (
              <div className="mt-4">
                <form onSubmit={handleVerifyEmail} className="space-y-3">
                  <input type="email" value={verifyEmail} onChange={(e) => { setVerifyEmail(e.target.value); setVerifyError(""); }}
                    placeholder="Enter your registered email" className={`w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#6a1fbf]/20 text-gray-900 ${verifyError ? "border-red-500" : "border-gray-300 focus:border-[#6a1fbf]"}`} />
                  {verifyError && <p className="text-red-500 text-xs mt-1">{verifyError}</p>}
                  <button type="submit" disabled={isVerifying} className="w-full py-2.5 rounded-lg font-semibold text-[#6a1fbf] border border-[#6a1fbf] hover:bg-[#6a1fbf]/5 transition-all disabled:opacity-50">
                    {isVerifying ? "Verifying..." : "Verify & Join"}
                  </button>
                  <button type="button" onClick={() => { setShowVerifyEmail(false); setVerifyError(""); }} className="w-full text-gray-500 text-sm hover:text-gray-700">Back to registration</button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    );

    return <Layout event={elp} formSlot={webinarFormSlot} webinar={webinar} />;
  }

  // ─── MAIN REGISTRATION PAGE (split layout) ───
  return (
    <div className="min-h-screen lg:h-screen bg-[#0D0D0D] flex flex-col lg:overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between h-14 px-6 border-b border-white/10 bg-black/50 backdrop-blur-sm">
        <RLinkIcon />
        <span className="text-gray-400 text-xs font-medium tracking-wider uppercase">
          {isPast ? "Replay" : "Event Landing Page"}
        </span>
      </header>

      {/* Main Content - Split Layout.
          Mobile: stacks vertically, image height is content-driven (aspect-ratio).
          Desktop (lg+): side-by-side, each panel is half the viewport. */}
      <div className="flex-1 flex flex-col lg:flex-row lg:h-[calc(100vh-56px)]">
        {/* Left Panel - Hero Image.
            On mobile: full width, aspect-[4/3] so the box auto-sizes; image uses
            object-contain so square / portrait / landscape uploads all show in full.
            On desktop: half-width, full panel height.
            A blurred copy of the same image fills the empty space behind the
            sharp image so the panel never shows ugly black bars regardless of
            the upload's aspect ratio. */}
        <div className="w-full lg:w-1/2 bg-black relative aspect-[4/3] lg:aspect-auto lg:h-full shrink-0 flex items-center justify-center overflow-hidden">
          {heroImage ? (
            <>
              {/* Blurred background fill */}
              <img
                src={heroImage}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 pointer-events-none"
              />
              {/* Sharp foreground image — always fully visible */}
              <img
                src={heroImage}
                alt={headline}
                className="relative z-[1] max-w-full max-h-full object-contain"
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${RLINK_PURPLE}40, #0D0D0D)` }}>
              <div className="text-center p-8">
                <div className="w-24 h-24 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${RLINK_PURPLE}20` }}>
                  <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke={RLINK_PURPLE} strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Form (scrollable independently) */}
        <div className="w-full lg:w-1/2 lg:overflow-y-auto flex items-start justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Title */}
            <div className="mb-8">
              <h1 className="text-white text-2xl lg:text-3xl font-bold mb-2">
                {isPast ? "Watch the Replay" : headline}
              </h1>
              {isPast && headline && (
                <p className="text-white text-sm font-medium mb-1">{headline}</p>
              )}
              {!isPast && description && (
                <p className="text-gray-400 text-sm">{description}</p>
              )}
              {isPast && description && (
                <p className="text-gray-400 text-xs mb-2">{description}</p>
              )}
              {isPast && (
                <p className="text-gray-400 text-sm">
                  {showReplayRegForm ? "Register to watch the replay." : "Enter your email to access the replay."}
                </p>
              )}
            </div>

            {/* Event details for replay page */}
            {isPast && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-purple-400 text-xs font-medium uppercase tracking-wider">Recorded Event</span>
                </div>
                <p className="text-white text-sm font-medium">{formatEventTime()}</p>
              </div>
            )}

            {/* Live Now Banner */}
            {!isPast && webinar.isLive && (
              <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <span className="text-green-400 text-sm font-semibold">This webinar is live now!</span>
                </div>
                <p className="text-gray-400 text-xs mt-1 ml-5">{webinar.requireRegistration ? "Register below to join immediately." : "Click below to join now."}</p>
              </div>
            )}

            {/* Event Time */}
            {!isPast && !webinar.isLive && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-purple-400 text-xs font-medium uppercase tracking-wider">Event Time</span>
                </div>
                <p className="text-white text-sm font-medium">{formatEventTime()}</p>
              </div>
            )}

            {/* Countdown Timer (only when not live). Hidden in the
                "You're registered" state — the confirmation block below
                renders its own countdown, so showing this one would
                duplicate the timer (ticket: back-to-back webinar issues). */}
            {!isPast && !webinar.isLive && webinar.countdownTimer && !showConfirmation && (
              <div className="flex gap-3 mb-8">
                {[
                  { val: timeLeft.days, label: "Days" },
                  { val: timeLeft.hours, label: "Hours" },
                  { val: timeLeft.minutes, label: "Min" },
                  { val: timeLeft.seconds, label: "Sec" },
                ].map(({ val, label }) => (
                  <div key={label} className="flex-1 text-center">
                    <div className="bg-white/5 border border-white/10 rounded-xl py-3">
                      <span className="text-white text-2xl font-bold">{formatTime(val)}</span>
                    </div>
                    <span className="text-gray-500 text-[10px] uppercase mt-1 block">{label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Registration / Replay Access Form */}
            {showConfirmation && !isPast ? (
              /* ── Already Registered — "You're Registered" + countdown.
                   Replaces the form in-place so the hero / branding /
                   event details above remain consistent with the rest
                   of the registration page. ── */
              <div className="space-y-5">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${RLINK_PURPLE}22` }}>
                    <CheckCircle className="w-8 h-8" style={{ color: RLINK_PURPLE }} />
                  </div>
                  <h3 className="text-white text-xl font-semibold mb-1">You're Registered!</h3>
                  {formData.email && (
                    <p className="text-gray-400 text-sm">Confirmation sent to <span className="text-white font-medium">{formData.email}</span></p>
                  )}
                </div>

                {webinar.isLive ? (
                  <>
                    <a
                      href={joinUrl || `/${webinar.roomId}`}
                      className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-90 text-base"
                      style={{ backgroundColor: RLINK_PURPLE }}
                    >
                      <Play className="w-5 h-5" />
                      Join Webinar Now
                    </a>
                    <p className="text-green-400 text-xs text-center font-medium">The webinar is live — click above to join.</p>
                  </>
                ) : (
                  <>
                    {/* Confirmation-block countdown. Always renders here
                        because the page-level countdown is hidden while
                        showConfirmation is true, so attendees still see
                        a timer even on webinars where the host disabled
                        the page-level countdown. */}
                    <div>
                      <p className="text-gray-400 text-xs text-center mb-2 uppercase tracking-wider">Webinar goes live in</p>
                      <div className="flex items-center justify-center divide-x divide-white/10 rounded-xl bg-white/5 border border-white/10">
                        {[
                          { value: timeLeft.days, label: 'Days' },
                          { value: timeLeft.hours, label: 'Hrs' },
                          { value: timeLeft.minutes, label: 'Min' },
                          { value: timeLeft.seconds, label: 'Sec' },
                        ].map((b) => (
                          <div key={b.label} className="flex flex-col items-center py-3 flex-1">
                            <span className="text-white text-2xl font-bold tabular-nums">{String(b.value).padStart(2, '0')}</span>
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{b.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <CalendarShareDropdown
                      title={webinar.roomName || "Webinar"}
                      description={(() => {
                        const desc = webinar.description || webinar.registrationDescription || webinar.registrationSubtitle || "";
                        const link = joinUrl || `${window.location.origin}/${webinar.roomId}`;
                        const lines = [
                          `You're registered for: ${webinar.roomName || "Webinar"}`,
                          ``,
                          `Join here: ${link}`,
                        ];
                        if (desc) {
                          lines.push("", desc);
                        }
                        lines.push("", "This link is unique to you — please do not share it.");
                        return lines.join("\n");
                      })()}
                      startTime={webinar.scheduledStartTime}
                      endTime={webinar.scheduledEndTime}
                      durationMinutes={60}
                      location={joinUrl || `${window.location.origin}/${webinar.roomId}`}
                      filename={(webinar.roomName || "webinar").replace(/\s+/g, "-")}
                    />
                    <p className="text-gray-500 text-xs text-center">Your join link is included in the calendar event.</p>
                  </>
                )}
              </div>
            ) : !isPast && !webinar.requireRegistration ? (
              /* ── No Registration Required — info-only landing ── */
              <div className="space-y-4">
                {webinar.isLive ? (
                  <a
                    href={`/${webinar.roomId}`}
                    className="block w-full py-3.5 rounded-xl font-semibold text-white text-center transition-all hover:opacity-90"
                    style={{ backgroundColor: RLINK_PURPLE }}
                  >
                    Join Now
                  </a>
                ) : (
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center">
                    <p className="text-gray-400 text-sm">This event does not require registration.</p>
                    <p className="text-gray-500 text-xs mt-1">The join link will be available when the event goes live.</p>
                  </div>
                )}
              </div>
            ) : isPast && requiresReplayRegistration && !showReplayRegForm ? (
              /* ── Simplified Replay Access Form: Email + Passcode ── */
              <form onSubmit={async (e) => {
                e.preventDefault();
                const replayEmail = formData.email.trim();
                if (!replayEmail) {
                  setFormErrors({ email: "Email is required" });
                  return;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(replayEmail)) {
                  setFormErrors({ email: "Please enter a valid email" });
                  return;
                }
                setFormErrors({});
                setIsSubmitting(true);
                try {
                  const passcodeStr = passcode.join("");
                  const response = await userServices.verifyReplayAccess({
                    email: replayEmail,
                    meetingId: webinar.roomId,
                    passcode: webinar.requirePasscode ? passcodeStr : undefined,
                  });
                  if (response.success) {
                    if (response.recording) setRecordingUrl(response.recording);
                    setShowReplay(true);
                    localStorage.setItem(`webinar_reg_${webinar.roomId}`, JSON.stringify({ email: replayEmail }));
                    toast.success("Access granted!");
                  } else {
                    if (response.message?.toLowerCase().includes("passcode")) {
                      setPasscodeError("Invalid passcode");
                    } else {
                      setFormErrors({ email: "not_registered" });
                    }
                  }
                } catch (err) {
                  if (err.message?.toLowerCase().includes("passcode")) {
                    setPasscodeError("Invalid passcode");
                  } else {
                    setFormErrors({ email: "not_registered" });
                  }
                } finally {
                  setIsSubmitting(false);
                }
              }} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">
                    Registered Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your registered email"
                    className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                      formErrors.email
                        ? "border-red-500 focus:ring-red-500"
                        : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                    }`}
                  />
                  {formErrors.email && formErrors.email !== "not_registered" && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>
                  )}
                  {formErrors.email === "not_registered" && (
                    <div className="mt-2 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                      <p className="text-orange-300 text-xs mb-2">This email is not registered for this webinar.</p>
                      <button
                        type="button"
                        onClick={() => { setShowReplayRegForm(true); setFormErrors({}); }}
                        className="text-xs font-semibold underline hover:no-underline transition-colors"
                        style={{ color: RLINK_PURPLE }}
                      >
                        Register now to watch the replay
                      </button>
                    </div>
                  )}
                </div>

                {/* Inline Passcode (if required) */}
                {webinar.requirePasscode && (
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-1.5">
                      Passcode <span className="text-red-400">*</span>
                    </label>
                    <div className="flex justify-start gap-3">
                      {passcode.map((digit, i) => (
                        <input
                          key={i}
                          ref={passcodeRefs[i]}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handlePasscodeChange(i, e.target.value)}
                          onKeyDown={(e) => handlePasscodeKeyDown(i, e)}
                          className="w-12 h-12 text-center text-xl font-bold text-white bg-white/5 border border-white/20 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                        />
                      ))}
                    </div>
                    {passcodeError && (
                      <p className="text-red-400 text-xs mt-1">{passcodeError}</p>
                    )}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  style={{ backgroundColor: RLINK_PURPLE }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : "Watch Replay"}
                </button>

                {/* Not registered link */}
                {!formErrors.email && (
                  <p className="text-center text-gray-500 text-xs mt-4">
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={() => { setShowReplayRegForm(true); setFormErrors({}); }}
                      className="text-purple-400 hover:text-purple-300 underline hover:no-underline transition-colors"
                    >
                      Register here
                    </button>
                  </p>
                )}
              </form>
            ) : (
              /* ── Full Registration Form (for upcoming/live and replay registration) ── */
              <>
              <form onSubmit={showReplayRegForm ? handlePostRegister : handlePreRegister} className="space-y-4">
              {/* First Name */}
              {getFieldConfig("First Name").enabled && (
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">
                    First Name {getFieldConfig("First Name").required && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                      formErrors.firstName
                        ? "border-red-500 focus:ring-red-500"
                        : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                    }`}
                  />
                  {formErrors.firstName && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.firstName}</p>
                  )}
                </div>
              )}

              {/* Last Name */}
              {getFieldConfig("Last Name").enabled && (
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">
                    Last Name {getFieldConfig("Last Name").required && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                      formErrors.lastName
                        ? "border-red-500 focus:ring-red-500"
                        : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                    }`}
                  />
                  {formErrors.lastName && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.lastName}</p>
                  )}
                </div>
              )}

              {/* Email */}
              {getFieldConfig("Email Address").enabled && (
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">
                    Email Address {getFieldConfig("Email Address").required && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                      formErrors.email
                        ? "border-red-500 focus:ring-red-500"
                        : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                    }`}
                  />
                  {formErrors.email && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>
                  )}
                </div>
              )}

              {/* Phone Number */}
              {getFieldConfig("Phone Number").enabled && (
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">
                    Phone Number {getFieldConfig("Phone Number").required && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 000-0000"
                    className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                      formErrors.phone
                        ? "border-red-500 focus:ring-red-500"
                        : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                    }`}
                  />
                  {formErrors.phone && (
                    <p className="text-red-400 text-xs mt-1">{formErrors.phone}</p>
                  )}
                </div>
              )}

              {/* Custom Registration Fields */}
              {(webinar?.registrationFields || [])
                .filter(f => f.enabled && !["First Name", "Last Name", "Email Address", "Phone Number"].includes(f.name))
                .map((field) => (
                  <div key={field.name}>
                    <label className="block text-gray-300 text-sm font-medium mb-1.5">
                      {field.name} {field.required && <span className="text-red-400">*</span>}
                    </label>
                    {field.name === "Questions/Comments" ? (
                      <textarea
                        value={(formData.customFields || {})[field.name] || ""}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          customFields: { ...(prev.customFields || {}), [field.name]: e.target.value }
                        }))}
                        placeholder={field.name}
                        rows={3}
                        className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors resize-none ${
                          formErrors[`custom_${field.name}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                        }`}
                      />
                    ) : (
                      <input
                        type={field.name.toLowerCase().includes("email") || field.name.toLowerCase().includes("linkedin") ? "url" : "text"}
                        value={(formData.customFields || {})[field.name] || ""}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          customFields: { ...(prev.customFields || {}), [field.name]: e.target.value }
                        }))}
                        placeholder={field.name}
                        className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                          formErrors[`custom_${field.name}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                        }`}
                      />
                    )}
                    {formErrors[`custom_${field.name}`] && (
                      <p className="text-red-400 text-xs mt-1">{formErrors[`custom_${field.name}`]}</p>
                    )}
                  </div>
                ))}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                style={{ backgroundColor: RLINK_PURPLE }}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isPast ? "Getting Access..." : "Registering..."}
                  </span>
                ) : isPast ? (
                  "Watch Replay"
                ) : webinar.isLive ? (
                  "Register & Join Now"
                ) : (
                  "Register"
                )}
              </button>
            </form>

            {/* Back to email check (when in replay registration mode) */}
            {showReplayRegForm && (
              <p className="text-center text-gray-500 text-xs mt-4">
                Already registered?{" "}
                <button
                  type="button"
                  onClick={() => { setShowReplayRegForm(false); setFormErrors({}); }}
                  className="text-purple-400 hover:text-purple-300 underline hover:no-underline transition-colors"
                >
                  Verify your email instead
                </button>
              </p>
            )}

            {/* Already registered? Verify your email (always available, not just when live) */}
            {!isPast && !showReplayRegForm && !showVerifyEmail && webinar.requireRegistration && (
              <div className="mt-6 text-center">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 h-px bg-white/10"></div>
                  <span className="text-gray-500 text-xs uppercase">or</span>
                  <div className="flex-1 h-px bg-white/10"></div>
                </div>
                <button
                  onClick={() => setShowVerifyEmail(true)}
                  className="text-purple-400 text-base font-semibold hover:text-purple-300 transition-colors"
                >
                  Already registered? Verify your email to join
                </button>
              </div>
            )}

            {/* Verify Email Form */}
            {!isPast && showVerifyEmail && (
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-white/10"></div>
                  <span className="text-gray-500 text-xs uppercase">verify registration</span>
                  <div className="flex-1 h-px bg-white/10"></div>
                </div>
                <form onSubmit={handleVerifyEmail} className="space-y-3">
                  <div>
                    <input
                      type="email"
                      value={verifyEmail}
                      onChange={(e) => { setVerifyEmail(e.target.value); setVerifyError(""); }}
                      placeholder="Enter your registered email"
                      className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-1 transition-colors ${
                        verifyError
                          ? "border-red-500 focus:ring-red-500"
                          : "border-white/10 focus:border-purple-500 focus:ring-purple-500"
                      }`}
                    />
                    {verifyError && (
                      <p className="text-red-400 text-xs mt-1">{verifyError}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={isVerifying}
                    className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 border border-purple-500/50"
                    style={{ backgroundColor: 'rgba(131, 44, 255, 0.2)' }}
                  >
                    {isVerifying ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Verifying...
                      </span>
                    ) : (
                      "Verify & Join"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowVerifyEmail(false); setVerifyError(""); }}
                    className="w-full text-gray-400 text-sm hover:text-white transition-colors"
                  >
                    Back to registration
                  </button>
                </form>
              </div>
            )}
            </>
            )}

            {/* Powered By */}
            <div className="mt-10 flex items-center justify-center gap-2">
              <span className="text-gray-500 text-[10px] uppercase tracking-wider">Powered by</span>
              <span className="text-white text-xs font-semibold">R-Link</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// R-Link logo icon
function RLinkIcon() {
  return (
    <svg className="w-8 h-7" viewBox="0 0 35 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.8881 17.545C26.9885 17.1158 28.7936 12.5266 26.8103 9.19201C24.7489 5.72537 20.147 6.91393 16.7932 6.77086C16.2695 4.15162 15.0326 2.03862 12.8376 0.464879C16.8712 0.894082 21.0384 0.299801 25.072 0.773025C29.4956 1.27926 32.4372 3.0291 33.3175 7.55224C34.0306 11.261 33.9749 17.512 30.8216 20.1532C30.4761 20.4393 29.0833 21.1437 29.139 21.3638L35 31.2905L26.632 31.2244L21.2724 22.3212H16.7932L16.6483 17.622C16.8377 17.2918 16.9603 17.545 17.016 17.545C18.8211 17.545 21.161 17.7211 22.8881 17.545Z" fill="white" />
      <path d="M15.1886 25.7218C14.3195 33.2934 2.61989 34.438 0.413675 26.8334C-0.121165 24.9845 -0.165734 18.0292 0.447103 16.2353C1.61707 12.8457 5.32752 10.6667 8.92655 11.349C9.81795 11.5141 9.5171 14.0563 9.09368 14.6396C8.16886 15.9272 5.18267 15.52 4.80382 18.0842C4.60326 19.4819 4.58097 23.873 4.78154 25.2706C5.08238 27.4937 8.32485 28.231 9.84023 26.6573C10.9768 25.4687 10.4754 23.0806 10.7093 21.5839L13.2053 20.1312L15.1886 17.9632C14.9881 20.4173 15.4672 23.3007 15.1886 25.6998V25.7218Z" fill="white" />
      <path d="M5.94036 17.9081C5.94036 16.7086 10.0408 16.4995 10.4531 14.2544C10.7428 12.7027 10.7985 8.91688 10.6202 7.35414C10.3639 5.1421 8.30257 3.94253 6.23006 4.87797C4.04613 5.85743 5.01553 8.61974 4.6924 10.4576C2.74246 11.261 1.4165 12.5156 0.0682592 14.0783V6.48473C0.0682592 3.44729 3.83442 0.343822 6.7649 0.0576863C15.3892 -0.789715 15.6789 7.88239 15.1886 14.0123C14.8098 18.7996 10.5868 21.4738 5.94036 20.7365V17.9081Z" fill="white" />
    </svg>
  );
}

export default WebinarRegistrationPage;
